import { Sparkles, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GlobalSearch } from './GlobalSearch';
// import { LanguageMenu, ThemeMenu } from './PrefsMenu'; // hidden for now
import { useT } from '@/lib/i18n/useT';
import { FavoriteStar } from '@/features/favorites/FavoriteStar';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { useRightSidebar } from './RightSidebar';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { TourLauncher } from '@/features/tour/TourLauncher';

/**
 * Top bar — three-column grid:
 *  · Left   — WorkspaceSwitcher (`Company › Brand` pill + brand picker)
 *  · Center — GlobalSearch (events / brands / stations / stock)
 *  · Right  — help, notifications, favorite (theme/language hidden for now)
 *
 * Left and right columns are `flex-none` and `flex-1`-balanced so the
 * search stays visually centered regardless of the workspace pill width.
 */
export function TopBar() {
  const navigate = useNavigate();
  const t = useT();
  const toggleAssistant = useRightSidebar((s) => s.toggle);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 sm:gap-4 sm:px-6 md:px-8">
      {/* Left — workspace context (brand switcher pill). Hidden below lg
          to save space on mobile/tablet; falls back to sidebar context. */}
      <div className="hidden flex-1 lg:flex" data-tour="topbar-workspace">
        <WorkspaceSwitcher />
      </div>

      {/* Center — global search, kept visually centered by the flex-1
          flankers on each side. */}
      <div data-tour="topbar-search" className="min-w-0 flex-1 md:max-w-xl">
        <GlobalSearch className="w-full" />
      </div>

      {/* Right-side controls — aligned to the far right. */}
      <div className="flex flex-1 items-center justify-end gap-1.5 sm:gap-2 md:gap-3">
        {/* Theme + language switchers — temporarily hidden per design feedback.
            Re-enable by uncommenting and restoring the import above. */}
        {/* <div className="hidden items-center gap-2 md:flex md:gap-4">
          <ThemeMenu />
          <LanguageMenu />
        </div> */}

        {/* AI assistant — mobile/tablet only. On lg+ the docked right
            sidebar provides its own toggle, so this is hidden there. */}
        <button
          type="button"
          onClick={toggleAssistant}
          aria-label={t('assistant.expand')}
          className="grid h-10 w-10 flex-none place-items-center rounded-md text-[var(--color-xk-accent)] transition-colors hover:bg-[var(--color-xk-surface)] lg:hidden"
        >
          <Sparkles className="h-5 w-5" />
        </button>

        {/* Product tour — replays the current page's walkthrough. Self-hides
            on pages without a tour. */}
        <TourLauncher />

        {/* Help — always visible. Routes to the in-app Help Center (/help). */}
        <button
          type="button"
          data-tour="topbar-help"
          onClick={() => navigate('/help')}
          aria-label={t('topbar.help')}
          title={t('topbar.help')}
          className="grid h-10 w-10 flex-none place-items-center rounded-md text-[var(--color-xk-lavender)] transition-colors hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-xk-accent)]"
        >
          <HelpCircle className="h-5 w-5" />
        </button>

        {/* Notifications bell — always visible. */}
        <NotificationBell />

        {/* Favorite the current page — hidden on small phones. */}
        <div className="hidden sm:block">
          <FavoriteStar />
        </div>
      </div>
    </header>
  );
}
